import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { safeStorage } from '@/lib/utils';

/**
 * Pane workspace: the center column shows several terminal panes at once (chats, runs, org chart).
 * The URL reflects the focused pane; the list of open panes persists per browser.
 *
 * Pane keys: `runs` | `master` (company chat) | `chat:<conversationId>` | `run:<runId>`.
 * `/company` is its own page (overview + chat column), not a pane.
 * `/chat` itself is not a pane: it is the chats overview (conversation list + new chat).
 */
export type PaneKey = string;

export interface PaneMeta {
  agentId?: string | null;
  title?: string;
  status?: string | null;
}

export interface WorkspaceState {
  panes: PaneKey[];
  focused: PaneKey | null;
  expanded: PaneKey | null;
  meta: Record<PaneKey, PaneMeta>;
  /** Chat panes whose run finished while another pane had focus (cleared when the pane gets focus). */
  unread: Record<PaneKey, true>;
}

/** Conversations live in the pane grid; everything else (runs, a run) opens in the side drawer. */
export const isChatPane = (key: PaneKey) => key.startsWith('chat:');
export const isSidePane = (key: PaneKey) => key !== 'master' && key !== 'chat' && !isChatPane(key);

const ACTIVE_STATUSES = new Set([
  'queued',
  'running',
  'waiting_children',
  'awaiting_approval',
  'awaiting_human',
]);
const isActiveStatus = (s: string | null | undefined) => !!s && ACTIVE_STATUSES.has(s);

const withoutUnread = (unread: WorkspaceState['unread'], key: PaneKey | null) => {
  if (!key || !unread[key]) return unread;
  const { [key]: _read, ...rest } = unread;
  return rest;
};

type Action =
  | { type: 'open'; key: PaneKey }
  | { type: 'show'; key: PaneKey }
  | { type: 'close'; key: PaneKey }
  /** Close every pane for which `drop` is true (close all, close run views, close others). */
  | { type: 'closeWhere'; drop: (key: PaneKey) => boolean }
  | { type: 'focus'; key: PaneKey }
  | { type: 'expand'; key: PaneKey | null }
  | { type: 'meta'; key: PaneKey; meta: PaneMeta };

export const MAX_PANES = 8;
const STORE_KEY = 'af:workspace';

export function workspaceReducer(s: WorkspaceState, a: Action): WorkspaceState {
  switch (a.type) {
    case 'open': {
      if (s.panes.includes(a.key))
        return s.focused === a.key ? s : { ...s, focused: a.key, unread: withoutUnread(s.unread, a.key) };
      let panes = [...s.panes, a.key];
      // Evict the oldest non-focused pane beyond the cap.
      while (panes.length > MAX_PANES) {
        const victim = panes.find((p) => p !== a.key && p !== s.focused) ?? panes[0]!;
        panes = panes.filter((p) => p !== victim);
      }
      return {
        ...s,
        panes,
        focused: a.key,
        expanded: s.expanded && panes.includes(s.expanded) ? s.expanded : null,
        unread: withoutUnread(s.unread, a.key),
      };
    }
    case 'show': {
      // A conversation takes the place of the focused (else the last) chat pane: one chat at a time
      // unless the user explicitly opened one next to it ('open').
      if (s.panes.includes(a.key) || !isChatPane(a.key))
        return workspaceReducer(s, { type: 'open', key: a.key });
      const target = s.focused && isChatPane(s.focused) ? s.focused : [...s.panes].reverse().find(isChatPane);
      if (!target) return workspaceReducer(s, { type: 'open', key: a.key });
      const { [target]: _old, ...meta } = s.meta;
      return {
        panes: s.panes.map((p) => (p === target ? a.key : p)),
        focused: a.key,
        expanded: s.expanded === target ? a.key : s.expanded,
        meta,
        unread: withoutUnread(withoutUnread(s.unread, target), a.key),
      };
    }
    case 'close': {
      const idx = s.panes.indexOf(a.key);
      if (idx === -1) return s;
      const panes = s.panes.filter((p) => p !== a.key);
      const focused = s.focused === a.key ? (panes[Math.min(idx, panes.length - 1)] ?? null) : s.focused;
      const { [a.key]: _gone, ...meta } = s.meta;
      return {
        panes,
        focused,
        expanded: s.expanded === a.key ? null : s.expanded,
        meta,
        unread: withoutUnread(s.unread, a.key),
      };
    }
    case 'closeWhere': {
      const gone = s.panes.filter(a.drop);
      if (gone.length === 0) return s;
      const panes = s.panes.filter((p) => !a.drop(p));
      const meta = Object.fromEntries(Object.entries(s.meta).filter(([k]) => panes.includes(k)));
      const unread = Object.fromEntries(
        Object.entries(s.unread).filter(([k]) => panes.includes(k)),
      ) as WorkspaceState['unread'];
      return {
        panes,
        focused: s.focused && panes.includes(s.focused) ? s.focused : (panes[panes.length - 1] ?? null),
        expanded: s.expanded && panes.includes(s.expanded) ? s.expanded : null,
        meta,
        unread,
      };
    }
    case 'focus':
      return s.focused === a.key ? s : { ...s, focused: a.key, unread: withoutUnread(s.unread, a.key) };
    case 'expand':
      return { ...s, expanded: a.key, focused: a.key ?? s.focused, unread: withoutUnread(s.unread, a.key) };
    case 'meta': {
      const prev = s.meta[a.key];
      if (
        prev &&
        prev.agentId === a.meta.agentId &&
        prev.title === a.meta.title &&
        prev.status === a.meta.status
      )
        return s;
      const finishedInBackground =
        isChatPane(a.key) &&
        a.key !== s.focused &&
        isActiveStatus(prev?.status) &&
        !isActiveStatus(a.meta.status) &&
        a.meta.status !== 'idle';
      return {
        ...s,
        meta: { ...s.meta, [a.key]: { ...prev, ...a.meta } },
        unread: finishedInBackground ? { ...s.unread, [a.key]: true } : s.unread,
      };
    }
  }
}

/** Map a location to the pane it represents (null = regular page). */
export function paneFromPath(pathname: string): PaneKey | null {
  if (pathname === '/runs') return 'runs';
  if (pathname === '/chat') return 'chat';
  if (pathname === '/master') return 'master';
  const chat = /^\/chat\/([^/]+)$/.exec(pathname);
  if (chat) return `chat:${decodeURIComponent(chat[1]!)}`;
  const run = /^\/runs\/([^/]+)$/.exec(pathname);
  if (run) return `run:${decodeURIComponent(run[1]!)}`;
  return null;
}

export function pathForPane(key: PaneKey): string {
  if (key === 'runs' || key === 'chat' || key === 'master') return `/${key}`;
  const [kind, id] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
  return kind === 'chat' ? `/chat/${encodeURIComponent(id)}` : `/runs/${encodeURIComponent(id)}`;
}

interface Ctx {
  state: WorkspaceState;
  open: (key: PaneKey) => void;
  /** Open, or replace the focused conversation (navigation; `open` keeps both side by side). */
  show: (key: PaneKey) => void;
  close: (key: PaneKey) => void;
  closeWhere: (drop: (key: PaneKey) => boolean) => void;
  focus: (key: PaneKey) => void;
  expand: (key: PaneKey | null) => void;
  setMeta: (key: PaneKey, meta: PaneMeta) => void;
}

const WorkspaceContext = createContext<Ctx | null>(null);

function initial(): WorkspaceState {
  const saved = safeStorage.get<{ panes?: unknown }>(STORE_KEY);
  const panes = Array.isArray(saved?.panes)
    ? // `chat` was the old new-chat launcher pane.
      // `company` became its own page.
      (saved.panes.filter((p) => typeof p === 'string' && p !== 'chat' && p !== 'company') as string[]).slice(
        -MAX_PANES,
      )
    : [];
  return { panes, focused: panes[panes.length - 1] ?? null, expanded: null, meta: {}, unread: {} };
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(workspaceReducer, undefined, initial);
  useEffect(() => safeStorage.set(STORE_KEY, { panes: state.panes }), [state.panes]);
  const open = useCallback((key: PaneKey) => dispatch({ type: 'open', key }), []);
  const show = useCallback((key: PaneKey) => dispatch({ type: 'show', key }), []);
  const close = useCallback((key: PaneKey) => dispatch({ type: 'close', key }), []);
  const closeWhere = useCallback(
    (drop: (key: PaneKey) => boolean) => dispatch({ type: 'closeWhere', drop }),
    [],
  );
  const focus = useCallback((key: PaneKey) => dispatch({ type: 'focus', key }), []);
  const expand = useCallback((key: PaneKey | null) => dispatch({ type: 'expand', key }), []);
  const setMeta = useCallback((key: PaneKey, meta: PaneMeta) => dispatch({ type: 'meta', key, meta }), []);
  const value = useMemo(
    () => ({ state, open, show, close, closeWhere, focus, expand, setMeta }),
    [state, open, show, close, closeWhere, focus, expand, setMeta],
  );
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Ctx {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace outside WorkspaceProvider');
  return ctx;
}

/** Panes call this to publish their agent/title/status for the header and the right panel. */
export function usePaneMeta(key: PaneKey, meta: PaneMeta) {
  const setMeta = useContext(WorkspaceContext)?.setMeta;
  const { agentId, title, status } = meta;
  useEffect(() => {
    setMeta?.(key, { agentId, title, status });
  }, [setMeta, key, agentId, title, status]);
}
